import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AiFlowService, AiFlowSettings } from '../../../../services/core.ai-flow.service';
import { KnowledgeIndexAdminService } from './knowledge-index-admin.service';
import { UiService } from '../../../../services/core.ui.service';
import {
    KiConfig,
    KiSummary,
    KiSection,
    KiErrorPage,
    KiErrorItem,
} from './knowledge-index.types';
import { KnowledgeIndexQueueModalComponent } from './knowledge-index-queue-modal.component';

@Component({
    selector: 'knowledge-index-admin',
    template: require('./knowledge-index.component.html'),
})
export class KnowledgeIndexComponent implements OnInit, OnChanges {

    /** Numeric project DB ID or unixName. Never '0' — site level not supported by the backend yet. */
    @Input() projectId: number;

    /** When false the component is read-only; toggles and buttons are hidden. */
    @Input() canEdit: boolean = false;

    // ── Loading ───────────────────────────────────────────────────────────────
    loading = false;
    saving  = false;

    // ── Data ─────────────────────────────────────────────────────────────────
    summary:    KiSummary      | null = null;
    config:     KiConfig       | null = null;
    aiSettings: AiFlowSettings | null = null;

    // ── Error panel ──────────────────────────────────────────────────────────
    errorPanelOpen  = false;
    errorsLoading   = false;
    errorPage: KiErrorPage | null = null;
    errorPageNum  = 1;
    errorPageSize = 20;
    errorSortField = 'index_date';
    errorSortDir   = 'desc';

    // IDs selected for batch retry
    selectedIds: number[] = [];
    retrying = false;

    // Per-section re-index confirmation
    pendingReindexSection: string | null = null;
    reindexing: { [section: string]: boolean } = {};

    // choices for gf-choice-buttons
    readonly enabledChoices = { 'Enabled': true, 'Disabled': false };

    constructor(
        private aiFlowService: AiFlowService,
        private kiService: KnowledgeIndexAdminService,
        private uiService: UiService,
        private modalService: NgbModal,
    ) {}

    ngOnInit() {
        this.loadAll();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.projectId && !changes.projectId.firstChange) {
            this.loadAll();
        }
    }

    // ── Load ─────────────────────────────────────────────────────────────────

    loadAll() {
        this.loading    = true;
        this.summary    = null;
        this.config     = null;
        this.aiSettings = null;
        this.errorPage  = null;
        this.selectedIds = [];

        Promise.all([
            this.kiService.getSummary(this.projectId),
            this.aiFlowService.getKnowledgeIndexConfig(this.projectId),
            this.aiFlowService.getAiConfig(this.projectId),
        ]).then(([summary, config, aiSettings]) => {
            this.summary    = summary;
            this.config     = config;
            this.aiSettings = aiSettings;
            this.loading    = false;

            // Auto-expand error panel if there are errors
            const totalErrors = summary.sections.reduce((acc, s) => acc + s.error_count, 0);
            if (totalErrors > 0) {
                this.errorPanelOpen = true;
                this.loadErrors();
            }
        }).catch(err => {
            this.loading = false;
            this.uiService.message.showError(
                (err && err.error && err.error.message) || 'Failed to load knowledge index data'
            );
        });
    }

    loadErrors() {
        this.errorsLoading = true;
        this.kiService.getErrors(this.projectId, {
            page_num:   this.errorPageNum,
            page_size:  this.errorPageSize,
            sort_field: this.errorSortField,
            sort_dir:   this.errorSortDir,
        }).then(page => {
            this.errorPage    = page;
            this.errorsLoading = false;
        }).catch(err => {
            this.errorsLoading = false;
            this.uiService.message.showError(
                (err && err.error && err.error.message) || 'Failed to load indexing errors'
            );
        });
    }

    // ── Config toggles ───────────────────────────────────────────────────────

    saveSectionConfig(section: string, value: boolean) {
        if (!this.canEdit || !this.config) { return; }
        this.saving = true;
        const update: Partial<KiConfig> = { [section]: value } as any;
        this.aiFlowService.postKnowledgeIndexConfig(this.projectId, update)
            .then(updated => {
                this.config  = updated;
                this.saving  = false;
                this.uiService.message.showSuccess('Settings saved.');
            })
            .catch(err => {
                this.saving = false;
                this.uiService.message.showError(
                    (err && err.error && err.error.message) || 'Failed to save settings'
                );
            });
    }

    onMasterToggle(value: boolean) {
        this.saveSectionConfig('project', value);
    }

    onSectionToggle(section: string, value: boolean) {
        this.saveSectionConfig(section, value);
    }

    // ── Re-index ─────────────────────────────────────────────────────────────

    confirmReindex(section: string) {
        this.pendingReindexSection = section;
    }

    cancelReindex() {
        this.pendingReindexSection = null;
    }

    executeReindex(section: KiSection) {
        this.pendingReindexSection = null;
        this.reindexing[section.section] = true;
        this.kiService.reindex(this.projectId, { scope: 'section', section: section.section })
            .then(() => {
                this.reindexing[section.section] = false;
                this.loadAll();
            })
            .catch(err => {
                this.reindexing[section.section] = false;
                this.uiService.message.showError(
                    (err && err.error && err.error.message) || 'Re-index failed'
                );
            });
    }

    // ── Error retry ──────────────────────────────────────────────────────────

    toggleSelect(id: number) {
        const idx = this.selectedIds.indexOf(id);
        if (idx >= 0) {
            this.selectedIds.splice(idx, 1);
        } else {
            this.selectedIds.push(id);
        }
    }

    isSelected(id: number): boolean {
        return this.selectedIds.indexOf(id) >= 0;
    }

    retrySelected() {
        if (!this.selectedIds.length) { return; }
        this.retrying = true;
        this.kiService.reindex(this.projectId, { scope: 'errors', ids: [...this.selectedIds] })
            .then(() => {
                this.retrying    = false;
                this.selectedIds = [];
                this.loadAll();
            })
            .catch(err => {
                this.retrying = false;
                this.uiService.message.showError(
                    (err && err.error && err.error.message) || 'Retry failed'
                );
            });
    }

    retryAll() {
        this.retrying = true;
        this.kiService.reindex(this.projectId, { scope: 'errors', all: true })
            .then(() => {
                this.retrying    = false;
                this.selectedIds = [];
                this.loadAll();
            })
            .catch(err => {
                this.retrying = false;
                this.uiService.message.showError(
                    (err && err.error && err.error.message) || 'Retry all failed'
                );
            });
    }

    // ── Error panel paging / sorting ─────────────────────────────────────────

    toggleErrorPanel() {
        this.errorPanelOpen = !this.errorPanelOpen;
        if (this.errorPanelOpen && !this.errorPage) {
            this.loadErrors();
        }
    }

    goToErrorPage(pageNum: number) {
        this.errorPageNum = pageNum;
        this.loadErrors();
    }

    sortErrors(field: string) {
        if (this.errorSortField === field) {
            this.errorSortDir = this.errorSortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.errorSortField = field;
            this.errorSortDir   = 'desc';
        }
        this.errorPageNum = 1;
        this.loadErrors();
    }

    // ── Queue modal ──────────────────────────────────────────────────────────

    openQueueModal() {
        if (!this.summary) { return; }
        const modalRef = this.modalService.open(KnowledgeIndexQueueModalComponent);
        modalRef.componentInstance.projectId = this.projectId;
        modalRef.componentInstance.sections  = this.summary.sections;
        modalRef.componentInstance.close.subscribe((result: any) => {
            modalRef.close();
            if (result === true) {
                this.refreshSummary();
            }
        });
    }

    refreshSummary() {
        this.loading = true;
        this.kiService.getSummary(this.projectId)
            .then(summary => {
                this.summary = summary;
                this.loading = false;
                this.errorPage = null;
                this.errorPageNum = 1;
                this.loadErrors();
            })
            .catch(err => {
                this.loading = false;
                this.uiService.message.showError(
                    (err && err.error && err.error.message) || 'Failed to refresh summary'
                );
            });
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    get aiDisabled(): boolean {
        if (!this.aiSettings) { return false; }
        return this.aiSettings.ai_blocked_by !== null ||
               !this.aiSettings.ai_enabled ||
               this.aiSettings.ai_enabled.value !== true;
    }

    get aiDisabledReason(): string {
        if (!this.aiSettings) { return ''; }
        if (this.aiSettings.ai_blocked_by !== null) {
            return `AI Flow is disabled by ${this.aiSettings.ai_blocked_by}. Knowledge indexing is not available.`;
        }
        return 'AI Flow is not enabled for this project. Knowledge indexing is not available.';
    }

    get masterEnabled(): boolean {
        return this.config ? this.config.project : true;
    }

    get totalErrors(): number {
        if (!this.summary) { return 0; }
        return this.summary.sections.reduce((acc, s) => acc + s.error_count, 0);
    }

    get errorHasNextPage(): boolean {
        return this.errorPage ? this.errorPage.items.length === this.errorPageSize : false;
    }

    get errorHasPrevPage(): boolean {
        return this.errorPageNum > 1;
    }

    trackBySection(_index: number, section: KiSection): string {
        return section.section;
    }

    trackByError(_index: number, item: KiErrorItem): number {
        return item.id;
    }
}
